import { getInitialComment, getInitials, isSimpleRecurrence } from '@frontend/api-client';
import { useCallback, useEffect, useMemo, useVisibilityController } from '@frontend/react';
import {
  Divider,
  DropdownMenu,
  Icon,
  LoadingSpinnerPanel,
  PageContainer,
  XStack,
  YStack,
  type DropdownMenuProps,
} from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import {
  DeleteRecurrenceModal,
  HeaderButton,
  RecurrenceCard,
  TaskAttachmentModal,
  TaskChatMessageBubble,
} from '@frontend/web/components';
import {
  useAnalytics,
  useLazyRetrieveHouseholdQuery,
  useRetrieveClientQuery,
  useRetrieveRecurrenceQuery,
} from '@frontend/web/hooks';

import { TaskDetailModal } from '@frontend/web/components/DomainSpecific/Tasks/TaskDetailModal';
import { createFileRoute, useRouter } from '@tanstack/react-router';

const Recurrence = () => {
  const { recurrenceId } = Route.useParams();
  const router = useRouter();
  const { track } = useAnalytics();
  const { data: client } = useRetrieveClientQuery();
  const [retrieveHousehold, { data: household }] = useLazyRetrieveHouseholdQuery();
  const { data: recurrence, isLoading: isRecurrenceLoading } = useRetrieveRecurrenceQuery(recurrenceId);
  const [isDeleteRecurrenceModalOpen, { close: closeDeleteRecurrenceModal, open: openDeleteRecurrenceModal }] =
    useVisibilityController();
  const [isTaskDetailModalOpen, { close: closeTaskDetailModal, open: openTaskDetailModal }] = useVisibilityController();
  const [isTaskAttachmentModalOpen, { close: closeTaskAttachmentModal, open: openTaskAttachmentModal }] =
    useVisibilityController();

  useEffect(() => {
    if (client?.household) {
      retrieveHousehold(client.household);
    }
  }, [client?.household, retrieveHousehold]);

  const onClickDeleteRecurrence = useCallback(() => {
    openDeleteRecurrenceModal();
    track({ data: { recurrenceId }, event: 'deleteRecurrenceButtonSelected', topic: 'recurrences' });
  }, [openDeleteRecurrenceModal, track, recurrenceId]);

  const initialComment =
    recurrence && !isSimpleRecurrence(recurrence)
      ? getInitialComment(recurrence, client, {
          labelClassName: safeCss('text-caption text-textPrimary'),
          textClassName: safeCss('text-p-md text-textPrimary'),
        })
      : undefined;

  const dropdownMenuOptions = useMemo(() => {
    const options: DropdownMenuProps['layout'] = [
      {
        color: 'danger',
        icon: 'IconTrash',
        label: 'Delete this repeating task',
        onClick: onClickDeleteRecurrence,
        type: 'item',
      },
    ];
    if (household?.members?.length && recurrence && !isSimpleRecurrence(recurrence)) {
      // eslint-disable-next-line fp/no-mutating-methods
      options.push({
        icon: 'IconShare2',
        label: 'Share task',
        onClick: () => openTaskDetailModal(),
        type: 'item',
      });
    }
    if (recurrence && !isSimpleRecurrence(recurrence) && recurrence.attachments?.length) {
      // eslint-disable-next-line fp/no-mutating-methods
      options.push({
        icon: 'IconFile',
        label: 'Attachments',
        onClick: () => {
          if (recurrence.attachments.length) {
            openTaskAttachmentModal();
          }
        },
        type: 'item',
      });
    }
    return options;
  }, [household, recurrence, onClickDeleteRecurrence, openTaskDetailModal, openTaskAttachmentModal]);

  return (
    <PageContainer isScrollable={false} padding="none">
      <XStack className="flex items-center p-lg justify-between">
        <HeaderButton accessibilityLabel="Go back" iconName="IconChevronLeft" onClick={router.history.back} />
        <YStack className="grow-1">{recurrence ? <RecurrenceCard recurrence={recurrence} /> : null}</YStack>
        <DropdownMenu layout={dropdownMenuOptions}>
          <Icon
            accessibilityLabel="Open recurrence options menu"
            // eslint-disable-next-line tailwindcss/no-arbitrary-value
            className="bg-neutral-100 items-center justify-center size-[36px] rounded-full"
            name="IconDots"
          />
        </DropdownMenu>
      </XStack>
      {isRecurrenceLoading ? (
        <LoadingSpinnerPanel fullScreen />
      ) : (
        <YStack className="px-lg pb-xxxl gap-y-xl flex-1 justify-end">
          {initialComment && client ? (
            <TaskChatMessageBubble
              {...initialComment}
              authorInfo={{ initials: getInitials(client), label: 'You', type: 'client' }}
              contentType="html"
              isAuthorVisible
            />
          ) : null}
          <YStack className="px-xl">
            <Divider />
          </YStack>
        </YStack>
      )}
      <DeleteRecurrenceModal
        isOpen={isDeleteRecurrenceModalOpen}
        onClose={closeDeleteRecurrenceModal}
        onComplete={router.history.back}
        recurrenceId={recurrenceId}
      />
      <TaskDetailModal
        isOpen={isTaskDetailModalOpen}
        onClose={closeTaskDetailModal}
        recurrence={recurrence && !isSimpleRecurrence(recurrence) ? recurrence : undefined}
      />
      <TaskAttachmentModal
        isOpen={isTaskAttachmentModalOpen}
        onClose={closeTaskAttachmentModal}
        recurrence={recurrence && !isSimpleRecurrence(recurrence) ? recurrence : undefined}
      />
    </PageContainer>
  );
};

export const Route = createFileRoute('/app/recurrence/$recurrenceId')({
  component: Recurrence,
});
